import {
  createFigmaCompanionPatchFromDesignMutation,
  verifyFigmaCompanionReadback,
  type FigmaCompanionPatch,
  type FigmaCompanionReceipt,
  type FigmaCompanionNodeAuthority,
} from "@cogo/design-data/figma-companion";
import type { DesignMutation } from "@cogo/design-data/design-mutation";

import type { ExternalDesignSyncBackEvidence } from "./external-design-source-state";
import type { OpenPencilFigmaCompanionMutationReadback } from "./openpencil-source-workspace";

export interface FigmaCompanionCheckpointInput {
  fileKey: string;
  version: string;
  hash: string;
  nodeIds: string[];
  nodeAuthorities: Record<string, FigmaCompanionNodeAuthority>;
}

export interface CreateFigmaCompanionSyncBackEnvelopeInput {
  mutation: DesignMutation;
  finalizedReadback: OpenPencilFigmaCompanionMutationReadback;
  checkpoint: FigmaCompanionCheckpointInput;
  patchId: string;
  idempotencyKey: string;
}

export interface FigmaCompanionSyncBackEnvelope {
  schemaVersion: "cogo.studio.figma-companion-sync-back-envelope.v1";
  patch: FigmaCompanionPatch;
  operatorConfirmation: {
    status: "required-in-figma-plugin";
    identitySource: "figma-current-user";
    requiredScopes: FigmaCompanionPatch["requiredOperatorScopes"];
  };
}

export interface FigmaCompanionSyncBackEvidence extends ExternalDesignSyncBackEvidence {
  receiptHash: string;
  authorityMode: "plugin-self-attested-selected-node-receipt";
}

export function createFigmaCompanionSyncBackEnvelope(
  input: CreateFigmaCompanionSyncBackEnvelopeInput,
): FigmaCompanionSyncBackEnvelope {
  if (
    input.finalizedReadback.mutationId !== input.mutation.mutationId ||
    input.finalizedReadback.mutationHash !== input.mutation.contentHash
  ) {
    throw new Error("FIGMA_COMPANION_FINALIZED_MUTATION_READBACK_MISMATCH");
  }
  const patch = createFigmaCompanionPatchFromDesignMutation({
    mutation: input.mutation,
    localRevision: {
      sourceId: input.finalizedReadback.source.sourceId,
      sourceRevision: input.finalizedReadback.source.sourceRevision,
      sha256: input.finalizedReadback.source.sha256,
    },
    checkpoint: input.checkpoint,
    patchId: input.patchId,
    idempotencyKey: input.idempotencyKey,
  });
  return {
    schemaVersion: "cogo.studio.figma-companion-sync-back-envelope.v1",
    patch,
    operatorConfirmation: {
      status: "required-in-figma-plugin",
      identitySource: "figma-current-user",
      requiredScopes: patch.requiredOperatorScopes,
    },
  };
}

export function deriveFigmaCompanionSyncBackEvidence(input: {
  patch: FigmaCompanionPatch;
  receipt: FigmaCompanionReceipt;
}): FigmaCompanionSyncBackEvidence {
  verifyFigmaCompanionReadback(input.patch, input.receipt);
  return {
    sourceId: input.patch.source.openPencilSourceId,
    sourceRevision: input.patch.source.openPencilSourceRevision,
    sourceSha256: input.patch.source.openPencilSourceSha256,
    fileKey: input.receipt.resultSource.fileKey,
    upstreamVersion: input.receipt.resultSource.version,
    syncedAt: input.receipt.appliedAt,
    receiptHash: input.receipt.contentHash,
    authorityMode: "plugin-self-attested-selected-node-receipt",
  };
}
